/**
 * src/components/map/TimelineSlider.tsx
 *
 * Dual-handle timeline range slider with Polarsteps step pins navigation.
 * Uses pointer-capture physics for smooth 60 FPS mobile scrubbing.
 */

import React, { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import { PolarstepsStep } from '../../types/polarsteps';
import {
  Calendar,
  Clock,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Minimize2,
  RotateCcw,
  MapPin,
} from 'lucide-react';

export interface TimelineSliderProps {
  minTime: number; // UTC Unix epoch seconds
  maxTime: number; // UTC Unix epoch seconds
  currentTimeRange: [number, number]; // [startUtc, endUtc]
  onChange: (range: [number, number]) => void;
  steps?: PolarstepsStep[];
  onStepSelect?: (step: PolarstepsStep) => void;
  timezoneId?: string;
  className?: string;
}

export const TimelineSlider: React.FC<TimelineSliderProps> = ({
  minTime,
  maxTime,
  currentTimeRange,
  onChange,
  steps = [],
  onStepSelect,
  timezoneId = 'UTC',
  className = '',
}) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [activeHandle, setActiveHandle] = useState<'start' | 'end' | null>(null);

  const rafIdRef = useRef<number | null>(null);
  const latestRangeRef = useRef<[number, number] | null>(null);

  useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, []);

  const duration = Math.max(1, maxTime - minTime);
  const rawStart = Math.min(currentTimeRange[0], currentTimeRange[1]);
  const rawEnd = Math.max(currentTimeRange[0], currentTimeRange[1]);
  const currentStart = Math.max(minTime, rawStart);
  const currentEnd = Math.min(maxTime, rawEnd);

  // Compute percentage positions
  const startPercent = Math.min(100, Math.max(0, ((currentStart - minTime) / duration) * 100));
  const endPercent = Math.min(100, Math.max(0, ((currentEnd - minTime) / duration) * 100));

  // Date and Time formatter
  const formatDateTime = useCallback(
    (epochSeconds: number): { date: string; time: string } => {
      try {
        const d = new Date(epochSeconds * 1000);
        const dateStr = new Intl.DateTimeFormat('it-IT', {
          timeZone: timezoneId,
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        }).format(d);
        const timeStr = new Intl.DateTimeFormat('it-IT', {
          timeZone: timezoneId,
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }).format(d);
        return { date: dateStr, time: timeStr };
      } catch {
        const d = new Date(epochSeconds * 1000);
        return { date: d.toLocaleDateString(), time: d.toLocaleTimeString() };
      }
    },
    [timezoneId]
  );

  // Pointer event scrub handlers
  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>, handle: 'start' | 'end') => {
      e.stopPropagation();
      e.preventDefault();
      const target = e.currentTarget;
      target.setPointerCapture(e.pointerId);
      setActiveHandle(handle);
    },
    []
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!activeHandle || !trackRef.current) return;
      e.stopPropagation();

      const rect = trackRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const ratio = Math.min(1, Math.max(0, clickX / rect.width));
      const targetEpoch = Math.round(minTime + ratio * duration);

      const minSeparationSeconds = Math.min(3600, duration * 0.02); // 1 hour or 2% of trip

      let nextRange: [number, number];
      if (activeHandle === 'start') {
        const newStart = Math.min(targetEpoch, currentEnd - minSeparationSeconds);
        nextRange = [Math.max(minTime, newStart), currentEnd];
      } else {
        const newEnd = Math.max(targetEpoch, currentStart + minSeparationSeconds);
        nextRange = [currentStart, Math.min(maxTime, newEnd)];
      }

      latestRangeRef.current = nextRange;

      if (rafIdRef.current === null) {
        rafIdRef.current = requestAnimationFrame(() => {
          rafIdRef.current = null;
          if (latestRangeRef.current) {
            onChange(latestRangeRef.current);
          }
        });
      }
    },
    [activeHandle, minTime, maxTime, duration, currentStart, currentEnd, onChange]
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (activeHandle) {
        e.currentTarget.releasePointerCapture(e.pointerId);
        setActiveHandle(null);
        if (rafIdRef.current !== null) {
          cancelAnimationFrame(rafIdRef.current);
          rafIdRef.current = null;
        }
        if (latestRangeRef.current) {
          onChange(latestRangeRef.current);
          latestRangeRef.current = null;
        }
      }
    },
    [activeHandle, onChange]
  );

  // Reset to full trip range
  const handleResetFullTrip = useCallback(() => {
    onChange([minTime, maxTime]);
  }, [minTime, maxTime, onChange]);

  // Step Pin calculations
  const stepPins = useMemo(() => {
    if (!steps || steps.length === 0 || duration <= 0) return [];
    return steps
      .filter((s) => s.start_time >= minTime && s.start_time <= maxTime)
      .map((step) => {
        const pct = ((step.start_time - minTime) / duration) * 100;
        return { step, pct };
      });
  }, [steps, minTime, maxTime, duration]);

  // Step jump navigation
  const currentStepIndex = useMemo(() => {
    if (steps.length === 0) return -1;
    for (let i = 0; i < steps.length; i++) {
      if (currentStart >= steps[i].start_time && currentStart <= (steps[i].end_time || steps[i].start_time + 86400)) {
        return i;
      }
    }
    return 0;
  }, [steps, currentStart]);

  const handlePrevStep = useCallback(() => {
    if (steps.length === 0) return;
    const prevIdx = Math.max(0, currentStepIndex - 1);
    const targetStep = steps[prevIdx];
    const stepEnd = targetStep.end_time || targetStep.start_time + 86400;
    onChange([targetStep.start_time, stepEnd]);
    if (onStepSelect) onStepSelect(targetStep);
  }, [steps, currentStepIndex, onChange, onStepSelect]);

  const handleNextStep = useCallback(() => {
    if (steps.length === 0) return;
    const nextIdx = Math.min(steps.length - 1, currentStepIndex + 1);
    const targetStep = steps[nextIdx];
    const stepEnd = targetStep.end_time || targetStep.start_time + 86400;
    onChange([targetStep.start_time, stepEnd]);
    if (onStepSelect) onStepSelect(targetStep);
  }, [steps, currentStepIndex, onChange, onStepSelect]);

  const startFormatted = formatDateTime(currentStart);
  const endFormatted = formatDateTime(currentEnd);

  // Collapsed Mode
  if (isCollapsed) {
    return (
      <div className={`pointer-events-auto flex items-center justify-between bg-slate-900/90 backdrop-blur-md border border-slate-700/80 rounded-full px-4 py-2 shadow-2xl text-xs text-slate-200 select-none ${className}`}>
        <div className="flex items-center gap-2">
          <Calendar className="w-3.5 h-3.5 text-sky-400" />
          <span>{startFormatted.date}</span>
          <span className="text-slate-500">—</span>
          <span>{endFormatted.date}</span>
        </div>
        <button
          type="button"
          onClick={() => setIsCollapsed(false)}
          className="ml-3 p-1 text-slate-400 hover:text-white transition cursor-pointer"
          title="Espandi Timeline"
          aria-label="Espandi Timeline"
        >
          <Maximize2 className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  // Expanded Mode
  return (
    <div
      className={`pointer-events-auto w-full bg-slate-900/95 backdrop-blur-lg border border-slate-700/80 rounded-2xl p-3 sm:p-4 shadow-2xl text-slate-100 select-none flex flex-col gap-3 ${className}`}
      role="region"
      aria-label="Timeline di viaggio"
    >
      {/* Top Header Row: Controls & Active Step / Date display */}
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-sky-400" />
          <span className="font-semibold text-slate-100">Filtro Temporale</span>
          <span className="text-[11px] text-slate-400 font-mono hidden sm:inline">
            ({timezoneId})
          </span>
        </div>

        <div className="flex items-center gap-1.5">
          {/* Previous / Next Step Buttons */}
          {steps.length > 0 && (
            <div className="flex items-center bg-slate-800/80 border border-slate-700/60 rounded-lg p-0.5 mr-1">
              <button
                type="button"
                onClick={handlePrevStep}
                disabled={currentStepIndex <= 0}
                className="p-1 text-slate-300 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition cursor-pointer"
                title="Tappa Precedente"
                aria-label="Tappa Precedente"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={handleNextStep}
                disabled={currentStepIndex >= steps.length - 1}
                className="p-1 text-slate-300 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition cursor-pointer"
                title="Tappa Successiva"
                aria-label="Tappa Successiva"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {/* Reset Full Trip Button */}
          <button
            type="button"
            onClick={handleResetFullTrip}
            className="flex items-center gap-1 px-2 py-1 bg-slate-800/80 hover:bg-slate-700 text-slate-300 hover:text-white border border-slate-700/60 rounded-lg text-[11px] transition cursor-pointer"
            title="Mostra intero viaggio"
          >
            <RotateCcw className="w-3 h-3 text-sky-400" />
            <span className="hidden sm:inline">Tutto</span>
          </button>

          {/* Collapse Button */}
          <button
            type="button"
            onClick={() => setIsCollapsed(true)}
            className="p-1 text-slate-400 hover:text-white transition cursor-pointer"
            title="Riduci a icona"
            aria-label="Riduci a icona"
          >
            <Minimize2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Date & Hour Badges */}
      <div className="flex items-center justify-between text-xs bg-slate-800/50 border border-slate-800 rounded-xl px-3 py-1.5">
        <div className="flex flex-col">
          <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">
            Inizio
          </span>
          <div className="flex items-center gap-1.5 font-medium text-sky-400 font-mono text-xs">
            <span>{startFormatted.date}</span>
            <span className="text-slate-500">•</span>
            <span className="flex items-center gap-0.5 text-slate-300">
              <Clock className="w-3 h-3 text-slate-400" />
              {startFormatted.time}
            </span>
          </div>
        </div>

        <div className="text-slate-500 font-light">—</div>

        <div className="flex flex-col text-right">
          <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">
            Fine
          </span>
          <div className="flex items-center gap-1.5 font-medium text-sky-400 font-mono text-xs justify-end">
            <span className="flex items-center gap-0.5 text-slate-300">
              <Clock className="w-3 h-3 text-slate-400" />
              {endFormatted.time}
            </span>
            <span className="text-slate-500">•</span>
            <span>{endFormatted.date}</span>
          </div>
        </div>
      </div>

      {/* Interactive Dual Slider Track with Step Pins */}
      <div className="relative pt-2 pb-1 px-1">
        <div
          ref={trackRef}
          className="relative h-2 bg-slate-800 rounded-full w-full cursor-pointer touch-none"
        >
          {/* Active Range Highlight */}
          <div
            className="absolute top-0 bottom-0 bg-gradient-to-r from-sky-500 to-indigo-500 rounded-full shadow-sm shadow-sky-500/50"
            style={{
              left: `${startPercent}%`,
              width: `${Math.max(1, endPercent - startPercent)}%`,
            }}
          />

          {/* Polarsteps Step Pins */}
          {stepPins.map(({ step, pct }) => (
            <button
              key={step.id}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                const stepEnd = step.end_time || step.start_time + 86400;
                onChange([step.start_time, stepEnd]);
                if (onStepSelect) onStepSelect(step);
              }}
              style={{ left: `${pct}%` }}
              className="group absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-amber-400 border border-slate-900 hover:scale-150 transition-transform cursor-pointer z-10"
              title={`${step.name} (${formatDateTime(step.start_time).date})`}
              aria-label={`Tappa: ${step.name}`}
            >
              {/* Tooltip on hover */}
              <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover:flex items-center gap-1 px-2 py-0.5 bg-slate-900 border border-slate-700 text-slate-200 text-[10px] rounded shadow-lg whitespace-nowrap z-30">
                <MapPin className="w-2.5 h-2.5 text-amber-400" />
                {step.name}
              </span>
            </button>
          ))}

          {/* Left Handle (Start Time) */}
          <div
            onPointerDown={(e) => handlePointerDown(e, 'start')}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            style={{ left: `${startPercent}%` }}
            tabIndex={0}
            role="slider"
            aria-valuemin={minTime}
            aria-valuemax={currentEnd}
            aria-valuenow={currentStart}
            aria-label="Orario di inizio intervallo"
            className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-white border-2 border-sky-500 shadow-md cursor-ew-resize touch-none flex items-center justify-center transition-transform z-20 ${
              activeHandle === 'start' ? 'scale-125 ring-4 ring-sky-500/30' : 'hover:scale-110'
            }`}
          >
            <div className="w-1.5 h-1.5 bg-sky-500 rounded-full" />
          </div>

          {/* Right Handle (End Time) */}
          <div
            onPointerDown={(e) => handlePointerDown(e, 'end')}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            style={{ left: `${endPercent}%` }}
            tabIndex={0}
            role="slider"
            aria-valuemin={currentStart}
            aria-valuemax={maxTime}
            aria-valuenow={currentEnd}
            aria-label="Orario di fine intervallo"
            className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-white border-2 border-indigo-500 shadow-md cursor-ew-resize touch-none flex items-center justify-center transition-transform z-20 ${
              activeHandle === 'end' ? 'scale-125 ring-4 ring-indigo-500/30' : 'hover:scale-110'
            }`}
          >
            <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimelineSlider;
